import { Network, Plus, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SortHeader, useEntitySort } from "@/components/EntitySort";
import { useApp } from "@/lib/AppContext";
import { ApiError, type Entity, type EntityType } from "@/lib/api";
import { ENTITY_TYPES, entityIcon, entityTypeLabel } from "@/lib/entityTypes";
import { formatDate } from "@/lib/format";
import { relationTypeMeta } from "@/lib/relationTypes";

/**
 * UI-6 — the entity manager. Every named thing the organization shares: people, products,
 * customers, projects and the rest, in one sortable table narrowed by type through the
 * navigator panel.
 *
 * The whole set is already here — the navigator's per-type counts read the same list — so
 * filtering and sorting happen in the browser rather than at the Server, exactly as the
 * recordings list does. Mentions and "last heard" are zero and blank until tagging fills
 * them (M5.1); that is a real answer for an org that has added context ahead of the
 * meetings that will reference it, not a gap to paper over.
 */
export function EntitiesPage() {
  const { entities, entitiesLoading, entitiesProblem } = useApp();
  const [searchParams] = useSearchParams();
  const [adding, setAdding] = useState(false);

  const typeFilter = searchParams.get("type");

  const filtered = useMemo(
    () => entities.filter((entity) => !typeFilter || typeFilter === "all" || entity.type === typeFilter),
    [entities, typeFilter],
  );
  const { sorted: shown, sort, toggleSort } = useEntitySort(filtered);

  return (
    <div className="flex flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-wrap items-start justify-between gap-[var(--space-4)]">
        <div className="flex flex-wrap items-baseline gap-[var(--space-4)]">
          <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            Entities
          </h1>
          <p className="max-w-[64ch] text-[length:var(--text-md)] text-[var(--text-secondary)]">
            Products, customers, suppliers and more — the organization's shared context.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110"
        >
          <Plus size={15} strokeWidth={2.5} />
          Add entity
        </button>
      </header>

      {/* 0035 — the one sentence that says how this table is used, and the one exception
          in it. Under the heading rather than buried in the Term row's own page, because
          the question it answers ("should I add our shorthand here?") is asked while
          looking at the list. */}
      <p className="max-w-[80ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Entities are the things you talk about. Aliases are what you call them. The transcriber
        is told the ones you say most — so a word like <em>Common</em> comes back as itself
        instead of as <em>comments</em>.{" "}
        <span className="text-[var(--text-muted)]">
          A <strong className="font-semibold text-[var(--text-secondary)]">Term</strong> is a word
          rather than a thing — your shorthand for something. It works like every other entity
          except that the world view never writes a page about one.
        </span>
      </p>

      {entitiesProblem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {entitiesProblem}
        </p>
      )}

      {entitiesLoading ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : shown.length === 0 ? (
        <Empty filtered={Boolean(typeFilter && typeFilter !== "all")} onAdd={() => setAdding(true)} />
      ) : (
        <div className="flex flex-col overflow-hidden rounded-[var(--radius-xl)] border border-[var(--border-subtle)]">
          <div className="grid grid-cols-[1.4fr_1.2fr_2fr_0.7fr_0.9fr] gap-3 bg-[var(--surface-2)] px-[var(--space-4)] py-2.5 text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wide)] text-[var(--text-muted)]">
            <SortHeader label="Name" active={sort} me="name" onClick={toggleSort} />
            <span>Aliases</span>
            <span>Description</span>
            <SortHeader label="Mentions" active={sort} me="mentions" onClick={toggleSort} />
            <SortHeader label="Last heard" active={sort} me="last" onClick={toggleSort} />
          </div>

          {shown.map((entity) => (
            <Row key={entity.id} entity={entity} />
          ))}
        </div>
      )}

      {adding && <AddEntityDialog onClose={() => setAdding(false)} />}
    </div>
  );
}

function Row({ entity }: { entity: Entity }) {
  const Icon = entityIcon(entity.type);
  const aliases = entity.aliases.length > 0 ? entity.aliases.join(", ") : "—";

  return (
    <Link
      to={`/entities/${entity.id}`}
      className="grid grid-cols-[1.4fr_1.2fr_2fr_0.7fr_0.9fr] items-center gap-3 border-t border-[var(--border-subtle)] px-[var(--space-4)] py-3 text-[length:var(--text-sm)] transition-[box-shadow,border-color] hover:border-[var(--border-default)] hover:shadow-[var(--shadow-md)]"
    >
      <span className="flex w-fit items-center gap-2 font-semibold text-[var(--vl-bridge-soft)]">
        <Icon size={14} strokeWidth={2} className="shrink-0" aria-hidden />
        <span className="truncate">{entity.name}</span>
      </span>
      <span className="truncate text-[length:var(--text-xs)] text-[var(--text-muted)]">{aliases}</span>
      <span className="truncate text-[var(--text-secondary)]">{entity.description ?? "—"}</span>
      <span className="tabular-nums text-[var(--text-muted)]">{entity.mentionCount}</span>
      <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
        {entity.lastMentionedAt ? formatDate(entity.lastMentionedAt) : "—"}
      </span>
    </Link>
  );
}

function Empty({ filtered, onAdd }: { filtered: boolean; onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] bg-[var(--surface-glass)] px-[var(--space-8)] py-[var(--space-12)] text-center backdrop-blur-[var(--blur-sm)]">
      <div className="mb-[var(--space-4)] flex h-12 w-12 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)]">
        <Network size={22} strokeWidth={2} className="text-[var(--text-muted)]" />
      </div>

      {filtered ? (
        <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
          Nothing of this type yet
        </p>
      ) : (
        <>
          <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">No entities yet</p>
          <p className="mt-1 max-w-[46ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            Add the people, products, customers and projects your meetings talk about, and the pipeline
            links mentions of them back here.
          </p>
          <button
            type="button"
            onClick={onAdd}
            className="mt-[var(--space-4)] inline-flex h-9 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110"
          >
            <Plus size={14} strokeWidth={2.5} />
            Add entity
          </button>
        </>
      )}
    </div>
  );
}

/**
 * The add-entity form (UI-6). A portal to the document body rather than an in-flow modal:
 * the content frame is zoomed and clips its overflow, and a `fixed` overlay inside it
 * would be positioned against the zoomed box rather than the window.
 */
function AddEntityDialog({ onClose }: { onClose: () => void }) {
  const { api, entities, refreshEntities } = useApp();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [type, setType] = useState<EntityType>("person");
  const [description, setDescription] = useState("");
  // 0072 — a feature is always a feature of a product, internal product or service, so it is
  // created with one; the Server refuses a feature without it.
  const [featureOf, setFeatureOf] = useState("");
  const offerings = entities
    .filter((entity) => FEATURE_OF_KINDS.includes(entity.type))
    .sort((a, b) => a.name.localeCompare(b.name));
  const isFeature = type === "feature";
  const ready = name.trim().length > 0 && (!isFeature || featureOf !== "");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit() {
    const trimmed = name.trim();
    if (!ready || busy) return;

    setBusy(true);
    setProblem(null);
    try {
      const created = await api.createEntity({
        type,
        name: trimmed,
        description: description.trim(),
        ...(isFeature ? { featureOf } : {}),
      });
      refreshEntities();
      // Straight into the new entity, so adding one and describing it fully is one motion.
      navigate(`/entities/${created.id}`);
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The entity could not be added.");
      setBusy(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[color-mix(in_oklch,var(--background)_60%,transparent)] p-4 backdrop-blur-[var(--blur-sm)]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add entity"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-[480px] rounded-[var(--radius-xl)] border border-[var(--border-default)] bg-[var(--surface-1)] p-[var(--space-6)] shadow-[var(--shadow-lg)]"
      >
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          <div>
            <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">Add entity</h2>
            <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              Give the system context it can link mentions to. Pick <em>Term</em> for a word you
              say rather than a thing you have.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        <div className="mt-[var(--space-6)] flex flex-col gap-[var(--space-4)]">
          <Field label="Name">
            <input
              ref={nameRef}
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && void submit()}
              placeholder="e.g. Van Dijk Logistics B.V."
              className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
            />
          </Field>

          <Field label="Type">
            <select
              value={type}
              onChange={(event) => setType(event.target.value as EntityType)}
              aria-label="Type"
              className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
            >
              {ENTITY_TYPES.map((meta) => (
                <option key={meta.type} value={meta.type}>
                  {meta.label}
                </option>
              ))}
            </select>
          </Field>

          {isFeature && (
            <Field label="Feature of">
              {offerings.length === 0 ? (
                <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
                  Add the product, internal product or service first — a feature belongs to one.
                </p>
              ) : (
                <select
                  value={featureOf}
                  onChange={(event) => setFeatureOf(event.target.value)}
                  aria-label="Feature of"
                  className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
                >
                  <option value="" disabled>
                    Pick a product, internal product or service…
                  </option>
                  {offerings.map((entity) => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name} ({entityTypeLabel(entity.type)})
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          <Field label="Description">
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              placeholder="Optional context for the extraction pipeline…"
              className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 py-2 text-[length:var(--text-sm)] leading-relaxed text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
            />
          </Field>

          {problem && (
            <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
              {problem}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || !ready}
              className="h-8 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Adding…" : "Add entity"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** What a feature can be a feature of — the object kinds of `feature_of`. */
const FEATURE_OF_KINDS: EntityType[] = relationTypeMeta("feature_of")!.objectKinds;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)]">{label}</span>
      {children}
    </label>
  );
}
